import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { AdHocGarmentModal } from './AdHocGarmentModal';
import { CatalogBrowser } from './CatalogBrowser';
import { ResultRail } from './ResultRail';
import { useCustomerSession } from './CustomerSession';
import { GarmentPreview } from './GarmentPreview';
import { MAX_CONCURRENT, useGenerationQueue, type GenerationTarget } from './useGenerationQueue';
import { useWakeLock } from '../pwa/useWakeLock';
import { loadPhoto } from '../lib/results-db';
import { Button } from '../ui/Button';
import { Alert } from '../ui/Alert';
import { Modal } from '../ui/Modal';
import type { GarmentSummary } from '../lib/types';

/**
 * The try-on surface: the customer's own photo, the catalogue, and the rail of
 * results.
 *
 * ⚠️ **A tap no longer generates.** It opens a confirmation showing the garment
 * and a Generate button, and only that button calls the API. Every generation
 * reserves quota and bills Gemini, so the old behaviour — tap a tile, money
 * moves — put a spend one stray finger away on a device handed to members of the
 * public.
 *
 * The photo is captured once and reused for every garment, so a customer can
 * work through as many as they like without going back to the camera. Retake
 * issues a fresh photo **inside the same visit**, which is why the results
 * already generated survive it.
 */
export function TryOnScreen() {
  const navigate = useNavigate();
  const { visitId, photoKey, endVisit } = useCustomerSession();
  const queue = useGenerationQueue(visitId, photoKey);
  const ownPhotoUrl = useOwnPhoto(photoKey);

  const [openKey, setOpenKey] = useState<string | null>(null);
  const [pending, setPending] = useState<GarmentSummary | null>(null);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [adHocOpen, setAdHocOpen] = useState(false);

  /**
   * Hold the screen awake for as long as anything is generating. A tablet that
   * sleeps mid-request suspends the page and kills the fetch — a paid image
   * lost with the customer standing there.
   */
  useWakeLock(queue.busy);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  // The photo is what gates this screen — the nav routes through capture first,
  // and this covers a direct URL or a session ended in another tab.
  if (!photoKey) return <Navigate to="/capture" replace />;

  const activeCount = queue.entries.filter(
    (entry) => entry.status === 'generating' || entry.status === 'queued',
  ).length;

  const open = queue.entries.find((entry) => entry.key === openKey);

  /**
   * Queue a generation and say so if it will have to wait.
   *
   * ⚠️ Shared by the confirmation modal and the ad-hoc sheet on purpose. The
   * concurrency notice used to live inline in the modal's `onClick`, so a
   * second caller would have silently queued past `MAX_CONCURRENT` with no
   * word to the customer — the one case where nothing on screen moves for a
   * minute and there is nothing to read.
   */
  const queueGeneration = (target: GenerationTarget): void => {
    if (activeCount >= MAX_CONCURRENT) {
      setNotice(
        `Up to ${MAX_CONCURRENT} at a time — this one will start as soon as a slot frees up.`,
      );
    }
    queue.start(target);
  };

  return (
    <div className="flex flex-1 flex-col">
      <header className="flex items-center justify-between gap-3 px-4 pt-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-primary">Try on</h1>
          <p className="text-xs text-secondary">
            {activeCount > 0
              ? `${activeCount} generating — about a minute each`
              : 'Tap a garment, then confirm to generate'}
          </p>
        </div>
        <Button size="md" variant="ghost" onClick={() => setConfirmEnd(true)}>
          End session
        </Button>
      </header>

      {/* The customer's own photo, read back from the device — see `results-db`. */}
      <div className="flex items-center gap-3 px-4 pt-3">
        <button
          type="button"
          onClick={() => ownPhotoUrl && setPhotoOpen(true)}
          disabled={!ownPhotoUrl}
          className="h-16 w-12 shrink-0 overflow-hidden rounded-md border border-(--color-border-subtle) bg-subtle"
        >
          {ownPhotoUrl ? (
            <img src={ownPhotoUrl} alt="Your photo" className="h-full w-full object-cover" />
          ) : (
            <span className="text-[10px] text-tertiary">No preview</span>
          )}
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-primary">Your photo</p>
          <p className="text-xs text-tertiary">Used for every garment you try.</p>
        </div>
        <Button size="md" variant="secondary" onClick={() => void navigate('/capture')}>
          Retake
        </Button>
      </div>

      {/*
        ⚠️ **Ad-hoc try-on, and its placement is the decision.** It sits beside
        the photo strip rather than inside the catalogue, because it is not a
        garment — putting it in the grid as a tile would make "photograph
        something" look like one more thing in stock, and would have to survive
        filtering and search that have nothing to say about it.

        `ghost`, not `primary`: the catalogue is still the better path for
        anything that will be in stock next week, so this is the escape hatch
        rather than the headline. It stays visible regardless — a salesman
        holding a new SKU should not have to remember where it lives.
      */}
      <div className="px-4 pt-2">
        <Button size="md" variant="ghost" onClick={() => setAdHocOpen(true)}>
          + Try something not in the catalogue
        </Button>
      </div>

      {notice && (
        <div className="px-4 pt-3">
          <Alert tone="info">{notice}</Alert>
        </div>
      )}

      <CatalogBrowser
        statusFor={(garmentId) => queue.entryFor(garmentId)?.status}
        onSelect={(garment) => {
          const existing = queue.entryFor(garment.garmentId);

          // A finished result opens rather than re-generating — the customer is
          // asking to see it again, not to buy a second one.
          if (existing?.status === 'done') {
            setOpenKey(existing.key);
            return;
          }
          /**
           * ⚠️ Queued or generating: **do nothing, say nothing.** The tile is
           * already showing "Working…" and the rail card is already spinning, so
           * a "that one is already on its way" notice was telling the customer
           * off for tapping something that visibly had not finished. Progress is
           * the answer to "is this working?", not a message.
           */
          if (existing && existing.status !== 'failed') return;
          setPending(garment);
        }}
      />

      <div className="sticky bottom-0 pb-safe">
        <ResultRail
          entries={queue.entries}
          activeKey={openKey}
          onOpen={setOpenKey}
          onRetry={queue.retry}
        />
      </div>

      {/*
        ⚠️ The confirmation. This modal's Generate button is one of exactly
        **two** things in the customer flow that spend money — the other is the
        ad-hoc sheet's, below — and both exist so that spending is a deliberate
        second action rather than a side effect of browsing. Nothing else may
        call `queueGeneration`, and a tap on a tile still only opens this.
      */}
      <Modal
        open={pending !== null}
        onClose={() => setPending(null)}
        title={pending?.name ?? 'Garment'}
        titleHidden
        footer={
          <>
            <Button variant="secondary" size="md" onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button
              size="md"
              onClick={() => {
                if (!pending) return;
                queueGeneration({
                  kind: 'catalogue',
                  garmentId: pending.garmentId,
                  name: pending.name,
                });
                setPending(null);
              }}
            >
              Generate
            </Button>
          </>
        }
      >
        {pending && (
          <div className="flex flex-col gap-3">
            <GarmentPreview garment={pending} />
            <p className="text-sm text-secondary">
              This takes about a minute. Your photo will be used to show you wearing this garment.
            </p>
          </div>
        )}
      </Modal>

      {/* Full-size view of one result. */}
      <Modal
        open={Boolean(open?.imageUrl)}
        onClose={() => setOpenKey(null)}
        title={open?.garmentName ?? 'Your try-on'}
        footer={
          <Button variant="secondary" size="md" onClick={() => setOpenKey(null)}>
            Close
          </Button>
        }
      >
        {open?.imageUrl && (
          <img
            src={open.imageUrl}
            alt={`You wearing ${open.garmentName}`}
            /**
             * ⚠️ **Capped, because `w-full` alone lets the image set its own
             * height.** A full-body try-on is taller than it is wide, so at the
             * modal's width it ran past the viewport and pushed the Close button
             * out of reach — and the dialog resized between results as the output
             * ratio moved. `object-contain` keeps the whole garment visible
             * rather than cropping to fit the cap.
             */
            className="mx-auto max-h-[70vh] w-auto rounded-md object-contain"
          />
        )}
      </Modal>

      {/* Full-size view of the customer's own photo. */}
      <Modal
        open={photoOpen}
        onClose={() => setPhotoOpen(false)}
        title="Your photo"
        footer={
          <>
            <Button variant="secondary" size="md" onClick={() => setPhotoOpen(false)}>
              Close
            </Button>
            <Button size="md" onClick={() => void navigate('/capture')}>
              Retake
            </Button>
          </>
        }
      >
        {ownPhotoUrl && <img src={ownPhotoUrl} alt="Your photo" className="w-full rounded-md" />}
      </Modal>

      {/*
        The second spend button. It queues through the same `queueGeneration` as
        the tile confirmation above, so the concurrency notice, the rail card and
        every failure message are shared rather than reimplemented.
      */}
      <AdHocGarmentModal
        open={adHocOpen}
        onClose={() => setAdHocOpen(false)}
        onGenerate={queueGeneration}
      />

      <Modal
        open={confirmEnd}
        onClose={() => setConfirmEnd(false)}
        title="End this session?"
        footer={
          <>
            <Button variant="secondary" size="md" onClick={() => setConfirmEnd(false)}>
              Keep going
            </Button>
            <Button
              variant="danger"
              size="md"
              onClick={() => {
                // Order matters: drop the in-memory object URLs first, then let
                // `endVisit` clear what is on disk, then leave.
                queue.reset();
                void endVisit().then(() => navigate('/', { replace: true }));
              }}
            >
              End and clear
            </Button>
          </>
        }
      >
        <p className="text-sm text-secondary">
          This clears the photo and every try-on image from this device, ready for the next
          customer.
        </p>
      </Modal>
    </div>
  );
}

/**
 * Reads the customer's own photo back off the device as an object URL.
 *
 * 🔒 The photo is not fetchable from R2 by the browser — it lands in the private
 * bucket and only the server can sign a read url for it. The blob is kept
 * locally at capture time precisely so this screen needs neither a new endpoint
 * nor a GET rule on that bucket's CORS policy.
 */
function useOwnPhoto(photoKey: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!photoKey) {
      setUrl(null);
      return;
    }

    let revoked: string | null = null;
    let cancelled = false;

    void loadPhoto(photoKey).then((blob) => {
      if (cancelled || !blob) return;
      revoked = URL.createObjectURL(blob);
      setUrl(revoked);
    });

    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
      setUrl(null);
    };
  }, [photoKey]);

  return url;
}
